import { useRef, useEffect } from 'react';
import { AlertTriangle, Trash2, X, Car } from 'lucide-react';

const ResetConfirmModal = ({ onConfirm, onCancel }) => {
  const modalRef = useRef(null);

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        onCancel();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onCancel]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onCancel]);

  return (
    <div className="modal-backdrop flex items-center justify-center p-4">
      <div 
        ref={modalRef}
        className="bg-white w-full max-w-sm rounded-2xl overflow-hidden animate-scale-in"
      >
        {/* Header */}
        <div className="p-6 bg-slate-50">
          <div className="flex justify-between items-start">
            <div className="w-12 h-12 rounded-xl bg-brand-100 flex items-center justify-center">
              <Car className="w-6 h-6 text-brand-600" />
            </div>
            <button
              onClick={onCancel}
              className="p-2 -m-2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          
          <h3 className="font-display font-semibold text-lg mt-4 text-slate-800">
            Start New Inspection?
          </h3>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-slate-600 text-sm">
            This will clear your current inspection and start fresh. All data will be deleted:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              All checklist statuses
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              Notes and photos
            </li>
            <li className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-slate-400" />
              Car profile information
            </li>
          </ul>
          
          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-xl flex gap-2">
            <AlertTriangle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-blue-700">
              <strong>Tip:</strong> Export your report before starting a new inspection if you want to keep a record.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="px-6 pb-6 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-3 px-4 rounded-xl border border-slate-200 text-slate-700 font-medium btn-touch"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="flex-1 py-3 px-4 rounded-xl bg-brand-500 hover:bg-brand-600 text-white font-semibold btn-touch"
          >
            Start Fresh
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResetConfirmModal;
